import { ArrowLeft, Check } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAcceptProposal, useClientRole, useDeclineProposal, useProposal, useProposals } from '@/api';
import { failureKind } from '@/lib/errors';
import { QueryPending } from '@/components/QueryState';
import { Dialog, Empty, Meta, PageHeader, Status } from '@/components/ui';
import { clientCan, OWNER_DECIDES } from '@/lib/access';
import { formatDate, formatDue, formatMoney } from '@/lib/format';

const statusView = {
  draft: { tone: 'neutral', label: 'Черновик' },
  sent: { tone: 'waiting', label: 'Ждёт вашего решения' },
  accepting: { tone: 'active', label: 'Принимается' },
  accepted: { tone: 'done', label: 'Принято' },
  declined: { tone: 'neutral', label: 'Отклонено' },
  withdrawn: { tone: 'neutral', label: 'Отозвано студией' },
  lost: { tone: 'neutral', label: 'Закрыто' },
} as const;

const reasons = ['Дорого', 'Не сейчас, вернёмся позже', 'Выбрали другое решение', 'Не то, что нужно'];

export function ProposalList() {
  const q = useProposals();
  const data = q.data;
  return (
    <>
      <PageHeader title="Предложения" description="Коммерческие предложения студии. Принятое предложение становится проектом со счётом на предоплату." />
      {!data ? (
        <QueryPending query={q} />
      ) : data.length === 0 ? (
        <Empty title="Предложений нет" />
      ) : (
        <ul className="divide-y divide-line border-y border-line">
          {data.map((p) => (
            <li key={p.id}>
              <Link to={`/proposals/${p.id}`} className="flex items-center gap-4 py-3 hover:bg-panel">
                <span className="flex-1">
                  <span className="block font-medium">{p.title}</span>
                  <span className="block text-sm text-muted">
                    {p.number}, {formatMoney(p.total)}
                  </span>
                </span>
                <Status tone={statusView[p.status].tone}>{statusView[p.status].label}</Status>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function Proposal() {
  const { proposalId = '' } = useParams();
  const navigate = useNavigate();
  const query = useProposal(proposalId);
  const { data: kp, error } = query;
  const accept = useAcceptProposal();
  const role = useClientRole();
  const decline = useDeclineProposal();
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState(reasons[0]);

  if (error && failureKind(error) === 'notFound') return <Empty title="Предложение не найдено" />;
  if (!kp) return <QueryPending query={query} />;
  const s = statusView[kp.status];
  const weeks = kp.milestones.reduce((n, m) => n + m.weeks, 0);
  const pre = Math.round((kp.total.amountMinor * kp.prepaymentPercent) / 100);

  return (
    <>
      <Link to="/proposals" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
        <ArrowLeft className="size-4" /> Предложения
      </Link>
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px]">
        <article className="min-w-0 max-w-2xl">
          <p className="text-sm text-muted">
            {kp.number}, от {formatDate(kp.sentAt ?? kp.validUntil)}
          </p>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">{kp.title}</h1>
          <p className="mt-4 leading-relaxed">{kp.intro}</p>

          <h2 className="mb-2 mt-8 text-sm font-semibold">Что входит</h2>
          <ul className="divide-y divide-line border-y border-line">
            {kp.lines.map((l, i) => (
              <li key={i} className="flex items-baseline gap-4 py-3">
                <span className="flex-1">
                  <span className="block font-medium">{l.title}</span>
                  {l.note && <span className="block text-sm text-muted">{l.note}</span>}
                </span>
                <span className="num">{formatMoney(l.total)}</span>
              </li>
            ))}
          </ul>

          <h2 className="mb-2 mt-8 text-sm font-semibold">План, {weeks} нед.</h2>
          <ol className="space-y-2">
            {kp.milestones.map((m, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="num grid size-5 shrink-0 place-items-center rounded-full text-[10px] font-semibold ring-1 ring-strong">{i + 1}</span>
                <span className="flex-1">{m.title}</span>
                <span className="text-muted">{m.weeks} нед.</span>
              </li>
            ))}
          </ol>
        </article>

        <aside className="lg:sticky lg:top-[80px] lg:self-start">
          <div className="frame px-6 py-6">
            <Status tone={s.tone}>{s.label}</Status>
            <div className="num mt-2 text-xl font-semibold tracking-tight">{formatMoney(kp.total)}</div>
            <p className="mt-1 text-sm text-muted">
              Предоплата {kp.prepaymentPercent} %, <span className="num">{formatMoney({ amountMinor: pre, currency: kp.total.currency })}</span>
            </p>
            {(kp.status === 'sent' || kp.status === 'accepting') &&
              (clientCan(role, 'admin') ? (
                <>
                  <button className="btn-primary mt-5 h-10 w-full" onClick={() => setAccepting(true)}>
                    <Check className="size-4" /> Принять и начать
                  </button>
                  <button className="btn-ghost mt-2 w-full" onClick={() => setDeclining(true)}>
                    Отказаться
                  </button>
                  <p className="mt-3 text-xs text-muted">После принятия появятся проект и счёт на предоплату. Вопросы можно задать в сообщениях — ответим до решения.</p>
                </>
              ) : (
                role && <p className="mt-5 text-sm text-muted">{OWNER_DECIDES}</p>
              )
            )}
            {kp.status === 'accepted' && kp.projectId && (
              <Link className="btn-secondary mt-5 w-full" to={`/p/${kp.projectId}`}>
                Открыть проект
              </Link>
            )}
          </div>
          <div className="mt-6">
            <Meta
              items={[
                { label: 'Действует', value: kp.status === 'sent' ? `до ${formatDate(kp.validUntil)}, ${formatDue(kp.validUntil)}` : formatDate(kp.validUntil) },
                ...(kp.declineReason ? [{ label: 'Причина отказа', value: kp.declineReason }] : []),
              ]}
            />
          </div>
        </aside>
      </div>
      <Dialog open={accepting} onClose={() => { setAccepting(false); accept.reset(); }} title="Принять предложение">
        <div className="space-y-3 p-5">
          <p className="text-sm text-muted">После принятия появятся проект и счёт на предоплату.</p>
          <Meta
            items={[
              { label: 'Сумма', value: <span className="num">{formatMoney(kp.total)}</span> },
              { label: 'Предоплата', value: <span className="num">{kp.prepaymentPercent} %, {formatMoney({ amountMinor: pre, currency: kp.total.currency })}</span> },
              { label: 'Действует до', value: formatDate(kp.validUntil) },
            ]}
          />
          {accept.error && <p className="text-sm text-overdue" role="alert">{(accept.error as Error).message}</p>}
          <button
            className="btn-primary mt-2 w-full"
            disabled={accept.isPending}
            onClick={() => accept.mutate(kp.id, { onSuccess: (x) => { setAccepting(false); if (x.projectId) navigate(`/p/${x.projectId}`); } })}
          >
            <Check className="size-4" /> Принять и начать
          </button>
        </div>
      </Dialog>
      <Dialog open={declining} onClose={() => { setDeclining(false); decline.reset(); }} title="Отказаться от предложения">
        <div className="space-y-3 p-5">
          <p className="text-sm text-muted">Подскажите почему — это поможет сделать следующее предложение точнее.</p>
          {reasons.map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm">
              <input type="radio" name="reason" checked={reason === r} onChange={() => setReason(r)} /> {r}
            </label>
          ))}
          <button className="btn-primary mt-2 w-full" onClick={() => decline.mutate({ id: kp.id, reason }, { onSuccess: () => setDeclining(false) })}>
            Отказаться
          </button>
          {decline.error && <p className="text-sm text-overdue" role="alert">{(decline.error as Error).message}</p>}
        </div>
      </Dialog>
    </>
  );
}
